import React from 'react'
import Link from 'next/link'
import { Breadcrumb } from '../commons/Breadcrumb'
import { Faq } from '../commons/Faq'
import { MarketHead } from './MarketHead'
import { MarketBooking } from './MarketBooking'
import { AnnualPanel, DrainFlushPanel, GasPanel, RepairPanel, ServicingPanel } from './AirconPanels'
import { AirconFullPriceTables } from './AirconFullPriceTables'
import { Checklist, InfoCards, IntroStrip, LinkCards, MarketSection, Prose, Steps } from './ui'
import { MarketAirconHero } from './MarketAirconHero'
import { MarketProcessSection, MarketReviewsSection } from './AirconSections'
import { HowToBook } from '../commons/service-hub/HowToBook'
import { WhyUs } from '../commons/service-hub/WhyUs'
import { AirconServices } from '../index/service-category/aircon-service/AirconServices'
import { AirconBrandsWeSupport } from '../index/service-category/aircon-service/AirconBrandsWeSupport'
import { airconHubPath, buildContext, guideLinks, resolveAll } from './content'
import { getAirconSections } from './sections'
import { FAQ_COUNTS, airconFaqBank, mergeFaqs, serviceFaqKind } from './faqs'
import { breadcrumbNode, faqNode, graph, serviceNode, webPageNode } from '../../utils/market-schema'
import { t } from '../../utils/markets'

// Which step-by-step process a service page shows (the Singapore subpages
// show the process for their service).
const processFor = (panel = {}) => {
    if (panel.type === 'annual') return 'general'
    if (panel.type === 'servicing' && panel.services?.length === 1) return panel.services[0]
    return null
}

const PricePanel = ({ panel, market, tables }) => {
    switch (panel.type) {
        case 'annual': return <AnnualPanel market={market} tables={tables} />
        case 'gas': return <GasPanel market={market} tables={tables} />
        case 'repair': return <RepairPanel market={market} tables={tables} problems={panel.problems} />
        case 'drainFlush': return <DrainFlushPanel market={market} tables={tables} />
        case 'priceTable': return <AirconFullPriceTables market={market} tables={tables} />
        default:
            return <ServicingPanel market={market} tables={tables} services={panel.services} defaultMount={panel.defaultMount} />
    }
}

// Free-form section from a content module: { title, intro, paragraphs, items,
// cards, steps, tone }. Lets each page carry its own structure instead of every
// page being the same template with the nouns swapped.
// `dark`: the slate theme of the Singapore personal trainer pages.
export const ContentSection = ({ section, id, dark = false }) => (
    <MarketSection id={id} title={section.title} intro={section.intro} tone={dark ? 'dark' : section.tone} narrow={!!section.narrow}>
        <div className="space-y-6">
            {section.paragraphs && <Prose paragraphs={section.paragraphs} dark={dark} />}
            {section.items && <Checklist items={section.items} columns={section.columns} dark={dark} />}
            {section.cards && <InfoCards items={section.cards} columns={section.cardColumns} dark={dark} />}
            {section.steps && <Steps items={section.steps} dark={dark} />}
        </div>
    </MarketSection>
)

/**
 * UAE / Malaysia aircon service page. `content` is a module from
 * data/markets/<cc>/aircon/<city>/<slug>.js; see the Dubai coil-cleaning page
 * for the full shape.
 */
export const AirconServicePage = ({ countryCd, citySlug, tables, requestCategorySettings, modifiedDate, testimonials = [], content: raw }) => {
    const ctx = buildContext({ countryCd, citySlug, tables })
    const { market, city } = ctx
    const content = resolveAll(raw, ctx)
    const sections = getAirconSections(ctx)
    const process = processFor(content.panel)
    const guides = guideLinks(market, citySlug, 'aircon', [content.slug])
    // Page questions, then this service's bank FAQs, then general market ones.
    const faqBank = airconFaqBank(ctx)
    const faqs = mergeFaqs(FAQ_COUNTS.page, content.faqs, faqBank.service[serviceFaqKind(content.panel)], faqBank.hub)
    const hubPath = airconHubPath(market, citySlug)
    const path = `${hubPath}/${content.slug}`

    const jsonLd = graph(
        webPageNode({ market, path, title: content.title, description: content.description, dateModified: modifiedDate, inLanguage: content.inLanguage }),
        breadcrumbNode(path, content.h1),
        serviceNode({
            market,
            city,
            path,
            name: content.schema?.name ?? content.h1,
            serviceType: content.schema?.serviceType,
            description: content.description,
            offers: content.schema?.offers ?? [],
        }),
        faqs.length ? faqNode(path, faqs) : null,
    )

    return (
        <>
            <MarketHead
                market={market}
                path={path}
                title={content.title}
                description={content.description}
                jsonLd={jsonLd}
                alternates={content.alternates}
                ogImageAlt={content.imageAlt}
            />
            <MarketBooking market={market} city={city} requestType="Aircon Services" requestCategorySettings={requestCategorySettings}>
                <MarketAirconHero
                    market={market}
                    tables={tables}
                    panel={content.panel}
                    title={content.h1}
                    subtitle={content.heroSubtitle || `Upfront prices across ${city.name}, travel included`}
                    image={content.image}
                    imageAlt={content.imageAlt}
                    pills={['Upfront prices', 'Travel included']}
                />
                <Breadcrumb pageTitle={content.h1} jsonLd={false} />
                <IntroStrip eyebrow={content.eyebrow} lead={content.lead} facts={content.facts} />

                <MarketSection id="prices" title={content.priceTitle} intro={content.priceIntro} tone="muted">
                    <PricePanel panel={content.panel} market={market} tables={tables} />
                </MarketSection>

                {process && <MarketProcessSection service={process} copy={sections.process[process]} />}

                {(content.sections ?? []).map((section, i) => (
                    <ContentSection key={i} id={section.id} section={section} />
                ))}

                <WhyUs id="ac-why-us" theme="dark" title={sections.whyUs.title} subtitle={sections.whyUs.subtitle} reasons={sections.whyUs.reasons} />
                <MarketReviewsSection testimonials={testimonials} />
                <HowToBook theme="light" title={sections.howToBook.title} steps={sections.howToBook.steps} />
                <AirconBrandsWeSupport market={market} intro={sections.brands.intro} linked={false} exclude={['Prism+']} />

                {faqs.length > 0 && (
                    <section className="max-w-4xl mx-auto px-4 py-10" aria-label="Frequently asked questions">
                        <Faq questionsAnswers={faqs} title={content.faqTitle || 'Questions people ask'} showSearch={false} jsonLd={false} />
                    </section>
                )}

                <AirconServices
                    services={sections.serviceCards.filter((card) => !card.href.endsWith(`/${content.slug}`))}
                    title={`More ${t(market, 'acShort')} services in ${city.name}`}
                    intro={sections.services.intro}
                />
                {guides.length > 0 && (
                    <MarketSection title="Guides" tone="muted">
                        <LinkCards links={guides} />
                    </MarketSection>
                )}
                <p className="max-w-5xl mx-auto px-4 py-6 text-sm text-slate-600">
                    Back to <Link href={hubPath} className="font-semibold text-orange-600 hover:underline">{t(market, 'acCategory')} {city.name}</Link>
                </p>

                {content.closingCta && (
                    <MarketSection narrow>
                        <div className="rounded-3xl bg-slate-900 p-8 text-center text-white">
                            <p className="text-xl font-bold">{content.closingCta.title}</p>
                            {content.closingCta.body && <p className="mt-2 text-slate-300">{content.closingCta.body}</p>}
                            <div className="mt-5">
                                <a href="#prices" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white transition hover:bg-orange-600">
                                    {content.closingCta.label || 'Choose units and book'}
                                </a>
                            </div>
                        </div>
                    </MarketSection>
                )}
                {/* Room for the sticky Book / WhatsApp bar on mobile */}
                <div aria-hidden="true" className="h-20 md:hidden" />
            </MarketBooking>
        </>
    )
}

